import { useMemo, useState } from 'react';

type CommonProps = {
  name: string;
  defaultValue?: string | null;
  value?: string;
  required?: boolean;
  disabled?: boolean;
  onChange?: (value: string) => void;
};

function dateParts(value?: string | null) {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? { year: match[1], month: match[2], day: match[3] } : { year: '', month: '', day: '' };
}

function timeParts(value?: string | null) {
  const match = String(value || '').match(/(?:T|^)(\d{2}):(\d{2})/);
  return match ? { hour: match[1], minute: match[2] } : { hour: '', minute: '' };
}

function daysInMonth(year: string, month: string) {
  if (!year || !month) return 31;
  return new Date(Number(year), Number(month), 0).getDate();
}

const months = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export function WebDateField({ name, defaultValue, value: controlledValue, required, disabled, onChange }: CommonProps) {
  const initial = dateParts(controlledValue ?? defaultValue);
  const [internal, setInternal] = useState(initial);
  const current = controlledValue === undefined ? internal : dateParts(controlledValue);
  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 81 }, (_, index) => String(currentYear + 10 - index));
  }, []);
  const dayCount = daysInMonth(current.year, current.month);
  const day = current.day && Number(current.day) <= dayCount ? current.day : '';
  const complete = current.year && current.month && day ? `${current.year}-${current.month}-${day}` : '';

  function update(part: 'year' | 'month' | 'day', next: string) {
    const updated = { ...current, [part]: next };
    const maximum = daysInMonth(updated.year, updated.month);
    if (updated.day && Number(updated.day) > maximum) updated.day = String(maximum).padStart(2, '0');
    const nextValue = updated.year && updated.month && updated.day ? `${updated.year}-${updated.month}-${updated.day}` : '';
    if (controlledValue === undefined) setInternal(updated);
    onChange?.(nextValue);
  }

  return <div className="web-date-field">
    <input type="hidden" name={name} value={complete} />
    <select aria-label="Day" value={day} required={required} disabled={disabled} onChange={(event) => update('day', event.target.value)}>
      <option value="">Day</option>{Array.from({ length: dayCount }, (_, index) => <option key={index + 1} value={String(index + 1).padStart(2, '0')}>{index + 1}</option>)}
    </select>
    <select aria-label="Month" value={current.month} required={required} disabled={disabled} onChange={(event) => update('month', event.target.value)}>
      <option value="">Month</option>{months.map((label, index) => <option key={label} value={String(index + 1).padStart(2, '0')}>{label}</option>)}
    </select>
    <select aria-label="Year" value={current.year} required={required} disabled={disabled} onChange={(event) => update('year', event.target.value)}>
      <option value="">Year</option>{years.map((year) => <option key={year} value={year}>{year}</option>)}
    </select>
  </div>;
}

export function WebTimeField({ name, defaultValue, value: controlledValue, required, disabled, onChange }: CommonProps) {
  const initial = timeParts(controlledValue ?? defaultValue);
  const [internal, setInternal] = useState(initial);
  const current = controlledValue === undefined ? internal : timeParts(controlledValue);
  const complete = current.hour && current.minute ? `${current.hour}:${current.minute}` : '';

  function update(part: 'hour' | 'minute', next: string) {
    const updated = { ...current, [part]: next };
    const nextValue = updated.hour && updated.minute ? `${updated.hour}:${updated.minute}` : '';
    if (controlledValue === undefined) setInternal(updated);
    onChange?.(nextValue);
  }

  return <div className="web-time-field">
    <input type="hidden" name={name} value={complete} />
    <select aria-label="Hour" value={current.hour} required={required} disabled={disabled} onChange={(event) => update('hour', event.target.value)}>
      <option value="">Hour</option>{Array.from({ length: 24 }, (_, index) => <option key={index} value={String(index).padStart(2, '0')}>{String(index).padStart(2, '0')}</option>)}
    </select>
    <span aria-hidden="true">:</span>
    <select aria-label="Minute" value={current.minute} required={required} disabled={disabled} onChange={(event) => update('minute', event.target.value)}>
      <option value="">Min</option>{Array.from({ length: 60 }, (_, minute) => <option key={minute} value={String(minute).padStart(2, '0')}>{String(minute).padStart(2, '0')}</option>)}
    </select>
  </div>;
}

export function WebDateTimeField({ name, defaultValue, required, disabled }: CommonProps) {
  const initialDate = String(defaultValue || '').slice(0, 10);
  const initialTime = timeParts(defaultValue);
  const [date, setDate] = useState(initialDate);
  const [time, setTime] = useState(initialTime.hour && initialTime.minute ? `${initialTime.hour}:${initialTime.minute}` : '');
  return <div className="web-datetime-field">
    <input type="hidden" name={name} value={date && time ? `${date}T${time}` : ''} />
    <WebDateField name={`${name}DateDisplay`} defaultValue={initialDate} required={required} disabled={disabled} onChange={setDate} />
    <WebTimeField name={`${name}TimeDisplay`} defaultValue={time} required={required} disabled={disabled} onChange={setTime} />
  </div>;
}
